import type { IntegrationType } from '../../../../services/integrations/core/IntegrationSystem';

export type ConnectionType =
  | 'continuous-sync'
  | 'on-demand-api'
  | 'archive-import'
  | 'file-import'
  | 'local-cli'
  | 'coming-soon';

export interface SourceDefinition {
  id: string;
  name: string;
  /** CSS gradient string used as background for the icon badge. */
  iconBg: string;
  /** Iconify icon name or simple-icons key, used by the node renderer. */
  iconName?: string;
  /** If set, the graph component will look up a live Integration entity by skeleton.integrationType. */
  integrationType?: IntegrationType;
  /**
   * Alternative lookup: match by entity.entityType instead of skeleton.integrationType.
   * Used for archive imports that create Integration.* entities (e.g. Integration.TwitterArchive)
   * which don't have a skeleton.integrationType field.
   */
  entityTypeLookup?: string;
  /**
   * Which skeleton field holds the relevant timestamp (defaults to 'lastSyncAt').
   * Archive imports use 'importedAt' instead.
   */
  syncTimestampField?: string;
  connectionType: ConnectionType;
  category: 'communication' | 'calendar' | 'storage' | 'finance' | 'social' | 'passwords' | 'documents' | 'crm' | 'analytics' | 'development';
  /** One-line description shown below the source name in the node card. */
  description: string;
  /** Rich explanation shown in the hover popover. Should be jargon-free. */
  helpText: string;
  /** Short pill label on the edge: e.g. "auto-sync", "on-demand". */
  connectionTypeLabel: string;
  /** Plain-English explanation of what this connection type means, shown in the edge label popover. */
  connectionTypeHelp: string;
}

// ── Connection-type explanations (shared across sources of the same type) ────

export const CONNECTION_TYPE_HELP: Record<ConnectionType, string> = {
  'continuous-sync':
    'River automatically syncs this source in the background on a regular schedule. Your data stays current and is always available to search, query, and share with your AI assistant without any action on your part.',
  'on-demand-api':
    'Pure API connection — River holds your credentials but does not ingest or store data from this source upfront. Instead, River queries it live in the moment your AI assistant needs it during a conversation or automated task. Think of it as giving River a key, not a copy.',
  'archive-import':
    'One-time import. You export a data archive from the service, upload it here, and River processes the full history into your knowledge base. Re-upload a fresh export whenever you want to refresh.',
  'file-import':
    'Manual file import. Drop or upload a supported file and River parses and indexes its contents. There is no live connection — you control exactly what gets added and when.',
  'local-cli':
    'A command-line tool installed on your own computer. River runs it in your terminal as you, so it sees exactly what you see — and your login stays on your machine rather than being stored by River. Because it is installed per computer, it only works on the machines you set it up on.',
  'coming-soon':
    'This integration is on the roadmap and not yet available. Stay tuned for updates.',
};

// ── Source definitions ────────────────────────────────────────────────────────

export const SOURCE_DEFINITIONS: SourceDefinition[] = [

  // ── Continuous-sync ─────────────────────────────────────────────────────

  {
    id: 'chrome-extension',
    name: 'Chrome Extension',
    iconBg: 'linear-gradient(160deg, #6db8ff, #4285F4)',
    iconName: 'simple-icons:googlechrome',
    integrationType: 'ChromeExtension',
    connectionType: 'continuous-sync',
    category: 'storage',
    description: 'Browsing history and captured web pages',
    helpText:
      'The River Chrome extension runs in the background and automatically pipes your browsing history and any pages you explicitly capture into your River knowledge base. It syncs roughly once per minute while your browser is open.',
    connectionTypeLabel: 'auto-sync',
    connectionTypeHelp: CONNECTION_TYPE_HELP['continuous-sync'],
  },

  {
    id: 'gmail',
    name: 'Gmail',
    iconBg: 'linear-gradient(160deg, #ff6d57, #EA4335)',
    iconName: 'simple-icons:gmail',
    integrationType: 'Gmail',
    connectionType: 'continuous-sync',
    category: 'communication',
    description: 'Email threads, contacts, and interaction history',
    helpText:
      'River connects to Gmail via Google OAuth and keeps your email activity continuously synced. It extracts contacts from your sent/received messages, builds interaction history, and makes all of it searchable inside River — without storing the full email content on River servers.',
    connectionTypeLabel: 'auto-sync',
    connectionTypeHelp: CONNECTION_TYPE_HELP['continuous-sync'],
  },

  {
    id: 'google-calendar',
    name: 'Google Calendar',
    iconBg: 'linear-gradient(160deg, #5fa8fb, #1a73e8)',
    iconName: 'simple-icons:googlecalendar',
    integrationType: 'GoogleCalendar',
    connectionType: 'continuous-sync',
    category: 'calendar',
    description: 'Meeting attendees, event history, and schedule context',
    helpText:
      'River reads your calendar via Google OAuth to surface meeting participants and upcoming events. It enriches your contact graph with people you meet regularly and gives your AI assistant awareness of your schedule so it can help you prep for calls, follow up after meetings, and understand your availability.',
    connectionTypeLabel: 'auto-sync',
    connectionTypeHelp: CONNECTION_TYPE_HELP['continuous-sync'],
  },

  {
    id: 'slack',
    name: 'Slack',
    iconBg: 'linear-gradient(160deg, #ed3278, #611f69)',
    iconName: 'simple-icons:slack',
    integrationType: 'Slack',
    connectionType: 'continuous-sync',
    category: 'communication',
    description: 'Direct message history from connected workspace',
    helpText:
      'River connects to your Slack workspace and syncs your DM history and public channel activity. This enriches your contact graph with people you communicate with regularly and lets your AI assistant reference conversations when answering questions or summarizing relationships.',
    connectionTypeLabel: 'auto-sync',
    connectionTypeHelp: CONNECTION_TYPE_HELP['continuous-sync'],
  },

  {
    id: 'plaid',
    name: 'Plaid Banking',
    iconBg: 'linear-gradient(160deg, #3b82f6, #1d4ed8)',
    iconName: 'ph:bank',
    integrationType: 'Plaid',
    connectionType: 'continuous-sync',
    category: 'finance',
    description: 'Bank transactions and account balances',
    helpText:
      'River connects to your bank via Plaid, a read-only banking data service trusted by thousands of apps. It syncs your transaction history so you can ask questions like "what did I spend on SaaS tools last month?" or automate expense categorization. Plaid never exposes your bank login credentials to River.',
    connectionTypeLabel: 'auto-sync',
    connectionTypeHelp: CONNECTION_TYPE_HELP['continuous-sync'],
  },

  {
    id: 'local-files',
    name: 'Local Files',
    iconBg: 'linear-gradient(160deg, #64748b, #334155)',
    iconName: 'ph:hard-drive',
    integrationType: 'LocalFiles',
    connectionType: 'continuous-sync',
    category: 'storage',
    description: 'Files auto-ingested from watched local folders',
    helpText:
      'River watches configured folders on your computer (e.g. Downloads, Documents) and automatically ingests new files as searchable entities. Works in the background while River is open. Configure watched folders and file type filters in Settings → Auto-Ingest.',
    connectionTypeLabel: 'auto-sync',
    connectionTypeHelp: CONNECTION_TYPE_HELP['continuous-sync'],
  },

  // ── On-demand API ────────────────────────────────────────────────────────

  {
    id: 'google-drive',
    name: 'Google Drive',
    iconBg: 'linear-gradient(160deg, #5bc87a, #34A853)',
    iconName: 'simple-icons:googledrive',
    integrationType: 'GoogleDrive',
    connectionType: 'on-demand-api',
    category: 'storage',
    description: 'Files and documents — searched on-demand',
    helpText:
      'River connects to Google Drive so your AI assistant can search your files when you ask it to. Unlike a sync, River does not copy your Drive contents — it queries Drive live each time, so you always get up-to-date results. Your files stay in Google; River just gets a window into them when needed.',
    connectionTypeLabel: 'on-demand',
    connectionTypeHelp: CONNECTION_TYPE_HELP['on-demand-api'],
  },

  {
    id: 'google-analytics',
    name: 'Google Analytics',
    iconBg: 'linear-gradient(160deg, #f5c542, #e67e22)',
    iconName: 'simple-icons:googleanalytics',
    integrationType: 'GoogleAnalytics',
    connectionType: 'on-demand-api',
    category: 'analytics',
    description: 'Website traffic and audience metrics',
    helpText:
      'River holds your Google Analytics API credentials and queries your website data live when your AI assistant needs it. Ask "how many visitors did my site get last week?" and River fetches the answer from Google in real time. No analytics data is stored in River.',
    connectionTypeLabel: 'on-demand',
    connectionTypeHelp: CONNECTION_TYPE_HELP['on-demand-api'],
  },

  {
    id: 'google-ads',
    name: 'Google Ads',
    iconBg: 'linear-gradient(160deg, #fbbc05, #ea4335)',
    iconName: 'simple-icons:googleads',
    integrationType: 'GoogleAds',
    connectionType: 'on-demand-api',
    category: 'analytics',
    description: 'Ad campaigns, spend, and performance data',
    helpText:
      'River stores your Google Ads API key and fetches campaign performance data on-demand. Ask your AI assistant about your ad spend, click-through rates, or budget pacing and River will pull the latest numbers from Google Ads at that moment. Nothing is pre-loaded or stored.',
    connectionTypeLabel: 'on-demand',
    connectionTypeHelp: CONNECTION_TYPE_HELP['on-demand-api'],
  },

  {
    id: 'google-search-console',
    name: 'Search Console',
    iconBg: 'linear-gradient(160deg, #5fa8fb, #4285F4)',
    iconName: 'simple-icons:googlesearchconsole',
    integrationType: 'GoogleSearchConsole',
    connectionType: 'on-demand-api',
    category: 'analytics',
    description: 'Search rankings, impressions, and click data',
    helpText:
      'River connects to Google Search Console via OAuth to fetch your site\'s search performance data when you ask. Want to know which keywords are driving traffic, or how your pages rank? River queries Search Console live and surfaces the answer. No data is stored between queries.',
    connectionTypeLabel: 'on-demand',
    connectionTypeHelp: CONNECTION_TYPE_HELP['on-demand-api'],
  },

  {
    id: 'github',
    name: 'GitHub',
    iconBg: 'linear-gradient(160deg, #555, #24292e)',
    iconName: 'simple-icons:github',
    integrationType: 'GitHub',
    connectionType: 'on-demand-api',
    category: 'development',
    description: 'Repositories, issues, and pull requests',
    helpText:
      'River uses your GitHub personal access token to fetch repository data, issues, and pull requests on demand. Ask your AI assistant to summarize recent PRs, list open issues, or describe a codebase and River will query GitHub live at that moment. No code or repo data is stored in River.',
    connectionTypeLabel: 'on-demand',
    connectionTypeHelp: CONNECTION_TYPE_HELP['on-demand-api'],
  },

  {
    id: 'apollo',
    name: 'Apollo',
    iconBg: 'linear-gradient(160deg, #7c3aed, #5b21b6)',
    iconName: 'ph:lightning',
    connectionType: 'on-demand-api',
    category: 'crm',
    description: 'Prospect and contact enrichment data',
    helpText:
      'River uses your Apollo API key to look up contact and company information on demand. When your AI assistant needs to enrich a contact with their job title, company size, or email address, River queries Apollo live. No Apollo data is stored between lookups.',
    connectionTypeLabel: 'on-demand',
    connectionTypeHelp: CONNECTION_TYPE_HELP['on-demand-api'],
  },

  {
    id: 'instantly',
    name: 'Instantly',
    iconBg: 'linear-gradient(160deg, #f97316, #ea580c)',
    iconName: 'ph:paper-plane-tilt',
    connectionType: 'on-demand-api',
    category: 'crm',
    description: 'Cold email campaigns and lead activity',
    helpText:
      'River connects to your Instantly account via API key to fetch campaign and lead data on demand. Ask your AI assistant about email open rates, reply rates, or which leads are engaged, and River queries Instantly live at that moment.',
    connectionTypeLabel: 'on-demand',
    connectionTypeHelp: CONNECTION_TYPE_HELP['on-demand-api'],
  },

  // ── Archive import ───────────────────────────────────────────────────────

  {
    id: 'twitter-x',
    name: 'Twitter / X Archive',
    iconBg: 'linear-gradient(160deg, #333, #000)',
    iconName: 'simple-icons:x',
    entityTypeLookup: 'Integration.TwitterArchive',
    syncTimestampField: 'importedAt',
    connectionType: 'archive-import',
    category: 'social',
    description: 'Followers, DMs, and tweet history from your export',
    helpText:
      'Twitter/X lets you export your full account history — tweets, DMs, followers, and following. Upload that ZIP file here and River will process it into your contact graph, importing people you\'ve interacted with and building a timeline of your activity on the platform.',
    connectionTypeLabel: 'archive',
    connectionTypeHelp: CONNECTION_TYPE_HELP['archive-import'],
  },

  {
    id: 'linkedin-csv',
    name: 'LinkedIn Connections',
    iconBg: 'linear-gradient(160deg, #2d87e0, #0A66C2)',
    iconName: 'simple-icons:linkedin',
    entityTypeLookup: 'Integration.LinkedInContacts',
    syncTimestampField: 'importedAt',
    connectionType: 'archive-import',
    category: 'social',
    description: 'Your connection list from LinkedIn data export',
    helpText:
      'LinkedIn lets you export your connections as a CSV from Settings → Get a copy of your data. Upload that CSV here and River will import your connections into your contact graph, complete with names, job titles, and companies.',
    connectionTypeLabel: 'archive',
    connectionTypeHelp: CONNECTION_TYPE_HELP['archive-import'],
  },

  // ── File import ──────────────────────────────────────────────────────────

  {
    id: '1password',
    name: '1Password',
    iconBg: 'linear-gradient(160deg, #5fa8fb, #0068d6)',
    iconName: 'simple-icons:1password',
    connectionType: 'file-import',
    category: 'passwords',
    description: 'Passwords and secure notes exported as CSV',
    helpText:
      'Export your 1Password vault as a CSV (File → Export) and import it into River\'s Password Vault app. River encrypts and stores your credentials locally — nothing is sent to external servers. You\'ll be able to search and autofill from River going forward.',
    connectionTypeLabel: 'file import',
    connectionTypeHelp: CONNECTION_TYPE_HELP['file-import'],
  },

  {
    id: 'dashlane',
    name: 'Dashlane',
    iconBg: 'linear-gradient(160deg, #6dc66a, #0e8b17)',
    iconName: 'simple-icons:dashlane',
    connectionType: 'file-import',
    category: 'passwords',
    description: 'Full ZIP export or individual credential CSVs',
    helpText:
      'Export your Dashlane data as a full ZIP archive or individual CSV files. River\'s Password Vault app can import credentials, payment cards, secure notes, and personal IDs from Dashlane exports.',
    connectionTypeLabel: 'file import',
    connectionTypeHelp: CONNECTION_TYPE_HELP['file-import'],
  },

  {
    id: 'bitwarden',
    name: 'Bitwarden / LastPass / KeePass',
    iconBg: 'linear-gradient(160deg, #5fa8fb, #175DDC)',
    iconName: 'simple-icons:bitwarden',
    connectionType: 'file-import',
    category: 'passwords',
    description: 'CSV export from any major password manager',
    helpText:
      'Most password managers let you export credentials as a CSV file. River\'s Password Vault app supports imports from Bitwarden, LastPass, KeePass, and other standard formats. Export from your manager\'s settings, then import here.',
    connectionTypeLabel: 'file import',
    connectionTypeHelp: CONNECTION_TYPE_HELP['file-import'],
  },

  {
    id: 'browser-passwords',
    name: 'Browser Passwords',
    iconBg: 'linear-gradient(160deg, #f5c542, #ea4335)',
    iconName: 'simple-icons:googlechrome',
    connectionType: 'file-import',
    category: 'passwords',
    description: 'Saved passwords from Chrome, Firefox, or Edge',
    helpText:
      'Chrome, Firefox, and Edge all let you export your saved passwords as a CSV from their settings pages. Import that file into River\'s Password Vault app to consolidate your browser-saved credentials in one secure place.',
    connectionTypeLabel: 'file import',
    connectionTypeHelp: CONNECTION_TYPE_HELP['file-import'],
  },

  {
    id: 'csv',
    name: 'CSV / Spreadsheets',
    iconBg: 'linear-gradient(160deg, #a0a0a0, #555)',
    iconName: 'ph:table',
    connectionType: 'file-import',
    category: 'documents',
    description: 'Import any CSV file into River Sheets',
    helpText:
      'Drag any CSV or spreadsheet file into a River Drive window (or use Sheets → Import) to bring tabular data into River. Once imported, your AI assistant can query, summarize, and analyze it.',
    connectionTypeLabel: 'file import',
    connectionTypeHelp: CONNECTION_TYPE_HELP['file-import'],
  },

  {
    id: 'word-docs',
    name: 'Word Documents',
    iconBg: 'linear-gradient(160deg, #5fa8fb, #2563eb)',
    iconName: 'ph:file-doc',
    connectionType: 'file-import',
    category: 'documents',
    description: '.docx and .rtf files imported as River Notes',
    helpText:
      'Drop a .docx or .rtf file into River Drive, or ask the agent to "import this Word doc." River parses the formatting and content into a River Note you can edit, search, and share.',
    connectionTypeLabel: 'file import',
    connectionTypeHelp: CONNECTION_TYPE_HELP['file-import'],
  },

  {
    id: 'google-docs',
    name: 'Google Docs',
    iconBg: 'linear-gradient(160deg, #5bc87a, #34A853)',
    iconName: 'simple-icons:googledocs',
    connectionType: 'file-import',
    category: 'documents',
    description: 'Import from a public Google Doc URL',
    helpText:
      'Share a Google Doc link with your AI assistant and River will fetch and import its contents as a River Note. The doc needs to be publicly viewable or shared with anyone with the link.',
    connectionTypeLabel: 'file import',
    connectionTypeHelp: CONNECTION_TYPE_HELP['file-import'],
  },

  // ── Coming soon ──────────────────────────────────────────────────────────

  {
    id: 'apple-contacts',
    name: 'Apple Contacts',
    iconBg: 'linear-gradient(160deg, #5bc87a, #34C759)',
    iconName: 'ph:address-book',
    connectionType: 'coming-soon',
    category: 'crm',
    description: 'Import your contacts from Apple Contacts (.vcf)',
    helpText:
      'Coming soon — export your contacts from Apple Contacts as a .vcf file and import them directly into River\'s contact graph.',
    connectionTypeLabel: 'coming soon',
    connectionTypeHelp: CONNECTION_TYPE_HELP['coming-soon'],
  },

  {
    id: 'facebook',
    name: 'Facebook',
    iconBg: 'linear-gradient(160deg, #5b8dee, #1877F2)',
    iconName: 'ph:facebook-logo',
    connectionType: 'coming-soon',
    category: 'social',
    description: 'Friends list and Messenger history via GDPR export',
    helpText:
      'Coming soon — Facebook\'s GDPR data export includes your friends list and Messenger history. River will be able to import these into your contact graph.',
    connectionTypeLabel: 'coming soon',
    connectionTypeHelp: CONNECTION_TYPE_HELP['coming-soon'],
  },

  {
    id: 'imessage',
    name: 'iMessage / SMS',
    iconBg: 'linear-gradient(160deg, #60e87a, #34C759)',
    iconName: 'ph:chat-circle',
    connectionType: 'coming-soon',
    category: 'communication',
    description: 'Message history from chat.db export',
    helpText:
      'Coming soon — macOS stores your iMessage and SMS history in a local database file. River will support importing this to enrich your contact graph with message history.',
    connectionTypeLabel: 'coming soon',
    connectionTypeHelp: CONNECTION_TYPE_HELP['coming-soon'],
  },

  {
    id: 'whatsapp',
    name: 'WhatsApp',
    iconBg: 'linear-gradient(160deg, #40e07a, #128C7E)',
    iconName: 'simple-icons:whatsapp',
    connectionType: 'coming-soon',
    category: 'communication',
    description: 'Business API message sync',
    helpText:
      'Coming soon — River will connect to WhatsApp Business API to sync your messaging history and contacts.',
    connectionTypeLabel: 'coming soon',
    connectionTypeHelp: CONNECTION_TYPE_HELP['coming-soon'],
  },
];
